import './index.css';
import {
    getCurrentUser,
    signIn,
    SignInOutput,
    signOut
} from 'aws-amplify/auth';
import { useCallback, useState, ReactNode, useEffect } from 'react';
import { selectConfig, setConfigThunk } from '../../app/configSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setTemporaryUser } from '../../app/userSlice';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { selectAuthUser } from '../../app/authSlice';
import { hydrateCognitoAuthUser } from '../../utils/auth/hydrateCognitoAuthUser';
import { FormHeader } from '../../components/formHeader';
import useAmplifyAuth from '../../hooks/useAmplifyAuth';
import IPage from '../../interfaces/page';
import { Navigate, useNavigate } from 'react-router-dom';
import { LoginType, ManualLoginDto } from '../../api-types/models/LoginDto';

export default function ManualLogin(
    props: IPage & { loadConfig?: boolean }
): ReactNode {
    // Hooks must always be called unconditionally (Rules of Hooks)
    useAmplifyAuth();

    const { t } = useTranslation();
    const {
        register,
        handleSubmit,
        formState: { isDirty, isValid }
    } = useForm<ManualLoginDto>({ mode: 'all' });

    const [passwordShown, setPasswordShown] = useState<boolean>(false);
    const [error, setError] = useState<string>();

    const config = useAppSelector(selectConfig);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useAppSelector(selectAuthUser);

    // Ensure Amplify is configured even if App.tsx hasn't dispatched it yet
    // (e.g. when MSAL is still initialising and blocks App.tsx's useEffect)
    useEffect(() => {
        if (!config && props.loadConfig !== false) {
            dispatch(setConfigThunk(undefined));
        }
    }, []);

    const togglePassword = useCallback(() => {
        setPasswordShown(!passwordShown);
    }, [passwordShown]);

    const handleGuestLogin = async (data: ManualLoginDto) => {
        try {
            if (config) {
                try {
                    await getCurrentUser();
                    await signOut();
                } catch {
                    // No active Cognito session (expected when switching from SSO).
                }

                await dispatch(setConfigThunk(undefined)).unwrap();

                localStorage.setItem('login', LoginType.BRUNCH);
                const signInResult: SignInOutput = await signIn({
                    username: data.email,
                    password: data.password
                });

                if (
                    signInResult.nextStep.signInStep ===
                    'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
                ) {
                    // Store user info for password reset flow
                    dispatch(
                        setTemporaryUser({
                            username: data.email,
                            challengeName: 'NEW_PASSWORD_REQUIRED'
                        })
                    );
                    navigate(
                        `/set-password?email=${encodeURIComponent(data.email)}`
                    );
                } else if (await hydrateCognitoAuthUser(dispatch)) {
                    navigate('/');
                }
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            setError(e.message);
        }
    };

    if (user) {
        return <Navigate to='/' />;
    }

    return (
        <div className='login-box'>
            <FormHeader />
            <div className='login-form'>
                <h1>{t('login_title')}</h1>
                <form>
                    <div>
                        <label>{t('email')}</label>
                        <input
                            className={`input-form ${
                                error === undefined ? '' : 'error-form'
                            }`}
                            type='email'
                            id='email'
                            {...register('email', { required: true })}
                        />
                    </div>
                    <div className='password-wrapper'>
                        <label>{t('password')}</label>
                        <input
                            className={`input-form ${
                                error === undefined ? '' : 'error-form'
                            }`}
                            type={passwordShown ? 'text' : 'password'}
                            id='password'
                            {...register('password', { required: true })}
                        />
                        <div
                            className='toggle-password'
                            onClick={togglePassword}
                        >
                            <img
                                alt='hide-password'
                                src='/images/ico_hide_password.svg'
                            />
                        </div>
                    </div>

                    {error !== undefined && (
                        <div className='error-toast'>
                            <img alt='error' src='/images/ico_error.svg' />
                            {error}
                        </div>
                    )}
                    <button
                        className='login-button'
                        role='login-button'
                        onClick={handleSubmit(handleGuestLogin)}
                        disabled={!isDirty || !isValid}
                    >
                        {t('login_title')}
                    </button>
                </form>
            </div>
            <div className='login-options'>
                <div
                    className='forgot-password'
                    onClick={() => navigate('/forgot-password')}
                >
                    {t('forgot_password')}
                </div>
            </div>
        </div>
    );
}
